import React, { useState, useEffect } from 'react';

export default function ResetPasswordScreen({ token, onBack, onResetSuccess }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);

  // Load custom background image from localStorage (set during last login)
  useEffect(() => {
    const savedBackground = localStorage.getItem('loginBackgroundImage');
    if (savedBackground) {
      setBackgroundImage(savedBackground);
    }
  }, []); // Load once on mount

  // Validate password strength
  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handleResetPassword = async () => {
    setError('');
    setSuccess('');

    // Validation
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setIsLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword,
          confirmPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.expired) {
          setError('This reset link has expired. Please request a new one.');
        } else {
          setError(data.error || 'Failed to reset password');
        }
        setIsLoading(false);
        return;
      }

      setSuccess('Password reset successful! Redirecting to login...');

      // Wait 2 seconds then redirect to login
      setTimeout(() => {
        if (onResetSuccess) {
          onResetSuccess(data.email);
        }
      }, 2000);

    } catch (error) {
      console.error('❌ Reset password error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="center-wrapper">
        <div className="container-3">
          <span className="logo"></span>
          <span className="title">INVEXIS</span>
          <div className="form">
            <div className="header" style={{color:"black"}}>Reset Password</div>
            <div className="instructions">
              Enter your new password below. Make sure it's strong and secure.
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                ✅ {success}
              </div>
            )}

            <div className="input-container">
              <input
                className="input"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                autoCapitalize="none"
                disabled={isLoading || success}
                style={{
                  border: '2px solid #000',
                  borderRadius: '8px',
                  width: '100%',
                  height: '48px',
                  padding: '0 40px 0 12px',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  color: 'black',
                  backgroundColor: 'white'
                }}
              />
              <button
                className="toggle-password"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPassword(!showPassword);
                }}
                type="button"
                style={{ transition: 'none' }}
              >
                <svg data-prefix="fas" data-icon="eye" className="svg-inline--fa fa-eye" role="img" viewBox="0 0 576 512" aria-hidden="true">
                  {showPassword ? (
                    <path fill="currentColor" d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"></path>
                    ) : (
                      <path fill="currentColor" d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"></path>
                    )}
                </svg>
              </button>
            </div>

            <div className="input-container">
              <input
                className="input"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                type={showConfirmPassword ? "text" : "password"}
                autoCapitalize="none"
                disabled={isLoading || success}
                style={{
                  border: '2px solid #000',
                  borderRadius: '8px',
                  width: '100%',
                  height: '48px',
                  padding: '0 40px 0 12px',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  color: 'black',
                  backgroundColor: 'white'
                }}
              />
              <button
                className="toggle-password"
                onClick={(e) => {
                  e.preventDefault();
                  setShowConfirmPassword(!showConfirmPassword);
                }}
                type="button"
                style={{ transition: 'none' }}
              >
                <svg data-prefix="fas" data-icon="eye" className="svg-inline--fa fa-eye" role="img" viewBox="0 0 576 512" aria-hidden="true">
                  {showConfirmPassword ? (
                    <path fill="currentColor" d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"></path>
                  ) : (
                    <path fill="currentColor" d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"></path>
                  )}
                </svg>
              </button>
            </div>

            <div className="password-requirements">
              <small>
                Password must:
                <ul>
                  <li>Must be at least 8 characters long</li>
                  <li>Must contain both uppercase and lowercase letters</li>
                  <li>Must include at least one number</li>
                  <li>Must include at least one special character (e.g., !@#$%)</li>
                </ul>
              </small>
            </div>

            <button
              className="resetButton"
              onClick={handleResetPassword}
              disabled={isLoading || success}
            >
              {isLoading ? 'Resetting...' : success ? '✓ Password Reset' : 'Reset Password'}
            </button>

            {!success && (
              <button className="backText" onClick={onBack} disabled={isLoading}>
                Back to Sign In
              </button>
            )}
          </div>
        </div>
      </div>
      <style>
        {`
          .center-wrapper {
            height: 100vh !important;
            min-height: unset !important;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f7f7f7;
          }
          .container-3 {
            max-width: ${backgroundImage ? '600px' : 'none'};
            width: 100vw;
            height: 100vh !important;
            min-height: unset !important;
            padding: 20px 20px 0 20px;
            font-family: Arial, sans-serif;
            background: ${backgroundImage ? `url('${backgroundImage}')` : '#fff'} no-repeat center center;
            background-size: cover;
            background-position: center;
            position: relative;
            --invexis-input-bg: #ffffff;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            margin: 0 0 3rem 0;
          }

          .logo {
            font-size: 48px;
            margin-bottom: 8px;
          }

          .title {
            color: black;
            font-size: 36.8px;
            font-weight: bold;
            letter-spacing: 2px;
            margin-bottom: 1.7rem;
          }

          .header {
            font-size: 24px;
            font-weight: bold;
            color: #000;
            margin-bottom: 8px;
            text-align: left;
          }

          .instructions {
            color: #555;
            font-size: 16px;
            margin-bottom: 16px;
            text-align: left;
          }

          .form {
            background-color: rgba(255, 255, 255, 0.95);
            padding: 30px;
            border-radius: 16px;
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          }

          .input-container {
            position: relative;
            margin-bottom: 12px;
          }

          .input {
            color: black !important;
            width: 100% !important;
            height: 2.8rem !important;
            border: 2px solid #000 !important;
            border-radius: 8px !important;
            padding: 0 40px 0 12px !important;
            font-size: 15px !important;
            box-sizing: border-box !important;
            background-color: white !important;
          }

          .input:-webkit-autofill,
          .input:-webkit-autofill:hover,
          .input:-webkit-autofill:focus,
          .input:-webkit-autofill:active {
            background-color: #ffffff !important;
            -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
            box-shadow: 0 0 0 1000px #ffffff inset !important;
            -webkit-text-fill-color: #000 !important;
            caret-color: #000 !important;
            border: 2px solid #000 !important;
            transition: background-color 999999s ease-out 0s !important;
          }

          .input:-moz-autofill {
            box-shadow: 0 0 0 1000px #ffffff inset !important;
            background-color: #ffffff !important;
            color: #000 !important;
          }

          .input:focus {
            outline: none !important;
            border: 2px solid #000 !important;
          }

          .input:disabled {
            background-color: #f0f0f0 !important;
            cursor: not-allowed;
            border: 2px solid #000 !important;
          }

          .toggle-password {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: none;
          }

          .toggle-password svg {
            width: 20px;
            height: 20px;
            color: #666;
            transition: none;
          }

          .toggle-password:hover svg {
            color: #000;
          }

          .toggle-password:active {
            transform: translateY(-50%);
          }

          .password-requirements {
            margin-bottom: 16px;
            padding: 12px;
            background-color: rgba(255, 255, 255, 0.8);
            border-radius: 8px;
            text-align: left;
          }

          .password-requirements small {
            color: #666;
            font-size: 12px;
          }

          .password-requirements ul {
            margin: 4px 0 0 0;
            padding-left: 20px;
          }

          .password-requirements li {
            margin: 2px 0;
          }

          .error-message {
            background-color: #fee;
            color: #c33;
            padding: 12px;
            border-radius: 8px;
            margin-bottom: 12px;
            text-align: center;
            font-size: 14px;
          }

          .success-message {
            background-color: #efe;
            color: #3a3;
            padding: 12px;
            border-radius: 8px;
            margin-bottom: 12px;
            text-align: center;
            font-size: 14px;
          }

          .resetButton {
            background-color: black;
            color: white;
            height: 48px;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            margin-top: 8px;
            transition: opacity 0.2s;
          }

          .resetButton:hover:not(:disabled) {
            opacity: 0.8;
          }

          .resetButton:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .backText {
            background-color: transparent;
            color: #000;
            border: none;
            font-size: 14px;
            margin-top: 16px;
            text-decoration: underline;
            cursor: pointer;
          }

          .backText:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}
      </style>
    </>
  );
}
